// DepartmentFaculty.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './FacultyDirectory.css';

// The exact order you want
const rankOrder = [
  'Chairman',
  'Dean',
  'Professor',
  'Associate Professor',
  'Assistant Professor',
  'Lecturer',
  'Teaching Assistant',
  'Lab Engineer',
  'Lab Assistant',
  'Lab Incharge',
  '',               // everyone else
];

// Utility: strip punctuation & lowercase
const normalize = str =>
  (str || '').toLowerCase();

// Utility: strip Engr./Dr./Prof. from start of a name for alpha sort
const stripPrefix = name =>
  (name || '')
    .replace(/^(engr\.?\s+|dr\.?\s+|prof\.?\s+)/i, '')
    .trim();

// Determine a member’s rank key
function getRankKey({ currentPortfolio, designation }) {
  const cp = normalize(currentPortfolio);
  const d = normalize(designation);

  // 1) Check portfolio for Chairman/Dean
  if (cp.includes('chairman') || cp.includes('chairperson')) {
    return 'Chairman';
  }
  if (cp.includes('dean')) {
    return 'Dean';
  }

  // 2) Check designation in the precise order
  //    Note: we test “Professor” *excluding* Associate/Assistant to keep plain Professors first.
  if (
    d.includes('professor') &&
    !d.includes('associate professor') &&
    !d.includes('assistant professor')
  ) {
    return 'Professor';
  }
  if (d.includes('associate professor')) {
    return 'Associate Professor';
  }
  if (d.includes('assistant professor')) {
    return 'Assistant Professor';
  }
  if (d.includes('lecturer')) {
    return 'Lecturer';
  }
  if (d.includes('teaching assistant') || d === 'ta') {
    return 'Teaching Assistant';
  }
  if (d.includes('lab engineer')) {
    return 'Lab Engineer';
  }
  if (d.includes('lab assistant')) {
    return 'Lab Assistant';
  }
  if (d.includes('lab incharge')) {
    return 'Lab Incharge';
  }

  // 3) Fallback: no rank
  return '';
}

export default function DepartmentFaculty() {
  const { id: departmentId } = useParams();

  const [department, setDepartment]   = useState(null);
  const [facultyMembers, setFaculty]  = useState([]);
  const [error, setError]             = useState(null);

  // 1) Fetch department details
  useEffect(() => {
    if (!departmentId) return;
    const token = localStorage.getItem('authToken') || '';

    fetch(`http://121.52.154.120:3001/api/departments/public/${departmentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then(data => {
        setDepartment(data);
        if (data.deptname) fetchFacultyByDeptName(data.deptname);
      })
      .catch(() => setError('Failed to fetch department details.'));
  }, [departmentId]);

  // 2) Fetch faculty for that department
  const fetchFacultyByDeptName = deptname => {
    const enc = encodeURIComponent(deptname);
    fetch(`http://121.52.154.120:3001/api/faculty/department/${enc}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then(arr => sortByRank(arr))
      .catch(() => setError('Failed to fetch faculty members.'));
  };

  // 3) Assign rankKey, then sort by (rankIndex, alphabetical name)
  const sortByRank = arr => {
    const withRanks = arr.map(m => ({
      ...m,
      rankKey: getRankKey(m),
    }));

    withRanks.sort((a, b) => {
      const ia = rankOrder.indexOf(a.rankKey);
      const ib = rankOrder.indexOf(b.rankKey);
      if (ia !== ib) return ia - ib;

      // same rank → alphabetical by name w/o prefix
      return stripPrefix(a.name).localeCompare(stripPrefix(b.name));
    });

    setFaculty(withRanks);
  };

  // 4) Render
  if (error) {
    return (
      <div className="department-faculty-page">
        <p className="error">{error}</p>
      </div>
    );
  }

  return (
    <div className="department-faculty-page">
      <div className="page-container container">
        <div className="content-area">
          {department ? (
            <>
              <h2 className="department-heading">
                {department.deptname || 'No Name Available'}
              </h2>
              {department.description && (
                <p className="department-description">
                  {department.description}
                </p>
              )}
            </>
          ) : (
            <p>Loading department details…</p>
          )}

          <h3 className="faculty-heading">Faculty Members</h3>
          <div className="faculty-row">
            {facultyMembers.length === 0 && <p>No faculty data available.</p>}

            {facultyMembers.map(m => (
              <div key={m.id} className="faculty-member">
                <Link to={`/faculty/${m.username || ''}`} className="faculty-link">
                  <h3 className="faculty-name">{m.name || 'Name not available'}</h3>

                  {m.fimages && (
                    <img
                      src={`http://121.52.154.120:3001${m.fimages}`}
                      alt={m.name}
                      className="faculty-image"
                    />
                  )}

                  <p className="faculty-designation">
                    {m.designation || 'Designation not available'}
                  </p>
                  <p className="faculty-currentPortfolio">
                    {m.currentPortfolio || 'Portfolio not available'}
                  </p>

                  <div className="contact-info">
                    <i className="fas fa-envelope" />
                    <span className="contact-text">
                      <a href={`mailto:${m.email || ''}`}>
                        {m.email || 'Email not available'}
                      </a>
                    </span>
                  </div>
                  <div className="contact-info">
                    <i className="fas fa-phone" />
                    <span className="contact-text">
                      {m.phoneNumber || 'Phone number not available'}
                    </span>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
